import React from 'react';

import {PB_ADMIN} from 'src/hooks/access-resource-constant';
import {useCurrentUser} from 'src/hooks/useCurrentUser';

import {AvatarWrapped} from '@pb/kit-component-avatar';
import Button from '@pb/kit-component-button';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import IconButton from '@pb/kit-component-icon-button';
import Typography from '@pb/kit-component-typography';

import {
    actions,
    header,
    idBlock,
} from '../user-profile.module.styl';

type Props = {
    displayName: string,
    userId?: number,
    avatarUrl?: string,
    onCopyId: () => void,
    onDelete: () => void,
    onSave: () => void,
    onSendLoginLink: () => void,
    onGenerateLoginLink: () => void,
    saveDisabled: boolean,
    deleteDisabled: boolean,
    saveBusy: boolean,
    deleteBusy: boolean,
    sendLoginLinkBusy: boolean,
    sendLoginLinkDisabled: boolean,
    generateLoginLinkBusy: boolean,
    generateLoginLinkDisabled: boolean,
};

export const UserProfileHeader: React.FC<Props> = ({
    displayName,
    userId,
    avatarUrl,
    onCopyId,
    onDelete,
    onSave,
    onSendLoginLink,
    onGenerateLoginLink,
    saveDisabled,
    deleteDisabled,
    saveBusy,
    deleteBusy,
    sendLoginLinkBusy,
    sendLoginLinkDisabled,
    generateLoginLinkBusy,
    generateLoginLinkDisabled,
}) => {
    const {hasAccessToResource} = useCurrentUser();
    const canDeleteUser = hasAccessToResource(PB_ADMIN.DELETE_USER);
    const canUpdateRole = hasAccessToResource(PB_ADMIN.UPDATE_ROLE);
    const canSendLoginLink = hasAccessToResource(PB_ADMIN.SEND_LOGIN_LINK);

    return (
        <div className={header}>
            <Group gap="m" alignItems="center">
                <AvatarWrapped
                    src={avatarUrl || ''}
                    alt={displayName}
                    name={displayName}
                    placeholder={Icons.PERSON_ROUNDED}
                />
                <div>
                    <div className="enable-selection">
                        <Typography style={{maxWidth: 350}} variant="heading-level-1">
                            <EllipsisTextHint title={displayName}>
                                {displayName}
                            </EllipsisTextHint>
                        </Typography>

                    </div>
                </div>
                <Group gap="s" alignItems="center">
                    <Group className={`${idBlock} enable-selection`} gap="s">
                        <Item>ID</Item>
                        <Item>{userId}</Item>
                    </Group>
                    <IconButton
                        onClick={onCopyId}
                        iconProps={{type: Icons.COPY}}
                    />
                </Group>
            </Group>
            <div className={actions}>
                {canDeleteUser ? (
                    <Button
                        variant="Type.SECONDARY"
                        disabled={deleteDisabled}
                        busy={deleteBusy}
                        colorVariant="ERROR"
                        onClick={onDelete}>
                        Delete
                    </Button>
                ) : null}
                {canSendLoginLink ? (
                    <Button
                        variant="Type.SECONDARY"
                        disabled={sendLoginLinkDisabled}
                        busy={sendLoginLinkBusy}
                        onClick={onSendLoginLink}>
                        Send Login Link
                    </Button>
                ) : null}
                {canSendLoginLink ? (
                    <Button
                        variant="Type.SECONDARY"
                        disabled={generateLoginLinkDisabled}
                        busy={generateLoginLinkBusy}
                        onClick={onGenerateLoginLink}>
                        Generate Login Link
                    </Button>
                ) : null}
                {canUpdateRole ? (
                    <Button
                        variant="Type.PRIMARY"
                        disabled={saveDisabled}
                        busy={saveBusy}
                        onClick={onSave}>
                        Save
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
